import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Ban,
  ClipboardCheck,
  PackageX,
  FileText,
  Keyboard,
  PackageCheck,
  Pencil,
  Plus,
  ScanLine,
  Send,
  Trash2,
  Undo2,
} from 'lucide-react';
import { ApiError, api, errorMessage } from '@/lib/api/client';
import type {
  CountQuestion,
  PurchaseOrder,
  PurchaseOrderItem,
  ReversePreview,
  ReversePreviewLine,
} from '@/lib/api/types';
import {
  Detail,
  DetailList,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Stat,
  StatRow,
} from '@/ui/Panel';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Callout } from '@/ui/Callout';
import { Badge, Code, Count } from '@/ui/Badge';
import { Dialog } from '@/ui/Dialog';
import { Checkbox, Field, Input, MoneyInput, QuantityInput, Textarea } from '@/ui/Field';
import { SubNav, SubNavItem } from '@/ui/Filters';
import { countLabel, money, num, qty, smartDate } from '@/lib/format';
import { cn } from '@/lib/cn';
import { checkQuantity, quantityForRequest } from '@/lib/quantity';
import {
  INVOICE_NUMBER_MAX_LENGTH,
  INVOICE_NUMBER_TOO_LONG_MESSAGE,
  quantityText,
} from '@kk-pos/shared/purchasing';
import { invalidateAfterReceive, n, poKeys } from '@/features/buying/api';
import {
  openOrdersFromError,
  recordedOnOrderFromError,
} from '@/features/buying/invoice/api';
import { InvoicesTab, billingSummary } from '@/features/buying/invoice/InvoicesTab';
import { EnterCostsPanel, useCostFacts } from '@/features/buying/EnterCostsPanel';
import { previewCost } from '@/features/buying/cost-entry';
import {
  MarkOrderedDialog,
  type MarkOrderedInput,
} from '@/features/buying/MarkOrderedDialog';
import { channelLabel } from '@/features/buying/channels';
import { OrderOutputPanel } from '@/features/buying/order-guide/OrderOutputPanel';
import {
  OrderSentPanel,
  keepOriginalCopy,
} from '@/features/buying/dispatch/OrderSentPanel';
import { dispatchKeys } from '@/features/buying/dispatch/api';
import { renderVendorOrder } from '@kk-pos/shared/vendor-order';
import { useVendorOrderFormat } from '@/features/buying/order-format/api';
import { orderFallbackEmail, orderFallbackPhone } from '@/features/buying/vendor-profile';
import { poOrderContext, poOrderLines } from '@/features/buying/order-format/lines';
import { OrderTable, UnplacedPanel } from '@/features/buying/order-format/OrderPreview';
import {
  CostWithFreightSwitch,
  FreightCells,
  PoFreightPanel,
  useCostWithFreightToggle,
} from '@/features/buying/freight/PoFreight';
import { usePoLanded } from '@/features/buying/freight/api';
import { useStoreName } from '@/features/buying/order-guide/api';
import { poItemName } from '@/features/buying/order-guide/poOrderText';
import { ScanInvoiceDialog } from '@/features/buying/invoice/ScanInvoiceDialog';
import { CancelOrderDialog } from '@/features/buying/CancelOrderDialog';
import { CloseShortDialog } from '@/features/buying/CloseShortDialog';
import { closedShortSummary } from '@/features/buying/close-short';
import { WhichOrderStep } from '@/features/buying/invoice/WhichOrderStep';
import type { OpenOrderRefusal } from '@/features/buying/invoice/types';
import {
  DuplicateInvoiceCallout,
  notesWithDuplicateAnswer,
  useDuplicateInvoiceCheck,
} from '@/features/buying/duplicate-invoice';
import { totalsByUnit, unitTotalsText } from '@/features/buying/unit-totals';
import {
  overReceiptConsequences,
  receiveLineFlags,
} from '@/features/buying/receive-warnings';
import {
  expectedLabel,
  expectedOnFromIso,
  isoFromExpectedOn,
} from '@/features/buying/dates';
import {
  CostVariance,
  PoStatusBadge,
  ProductPicker,
  poStatusLabel,
} from '@/features/buying/parts';
import { useDropOffLocations } from '@/features/buying/drop-off/api';
import { ChangeDeliveryDialog } from '@/features/buying/drop-off/ChangeDeliveryDialog';
import {
  DIRECT_LABEL,
  poDeliveryText,
  startLocationOf,
} from '@/features/buying/drop-off/model';
import { weekdayDayLabel } from '@/features/buying/order-guide/labels';
import type { PickedProduct } from '@/features/buying/parts';
import {
  CHANGED_SINCE_OPENED,
  CountConfirmation,
  DELIVERY_DATE_WINDOW_DAYS,
  addDays,
  answersToSend,
  countAnswerKey,
  countConfirmationConsequence,
  countQuestionsFromError,
  localToday,
  refusalCode,
  stockAnswer,
  storeDayLabel,
  unansweredLines,
  unansweredMessage,
  useCountCheck,
} from '@/features/buying/CountConfirmation';

type Mode = 'lines' | 'receive' | 'invoices' | 'costs';

/** TASK-0256: the statuses whose delivery can still change (Change delivery). */
const DELIVERY_OPEN = new Set(['draft', 'submitted', 'partially_received']);
export const Route = createFileRoute('/_app/purchasing/$id')({
  staticData: { crumb: 'Purchase order' },
  validateSearch: (s: Record<string, unknown>): { mode?: Mode; invoice?: number } => {
    const invoice = Number(s.invoice);
    return {
      mode:
        s.mode === 'receive'
          ? 'receive'
          : s.mode === 'invoices'
            ? 'invoices'
            : s.mode === 'costs'
              ? 'costs'
              : 'lines',
      ...(Number.isInteger(invoice) && invoice > 0 ? { invoice } : {}),
    };
  },
  /**
   * PO ids are integers. This route is the landing zone for the legacy
   * `/vendors/purchase-orders/*` → `/purchasing/*` rewrite, so it can be
   * handed an arbitrary trailing segment; a non-numeric one is a dead
   * bookmark, not a purchase order, and belongs on the not-found screen
   * rather than in a detail view showing an API validation message.
   */
  beforeLoad: ({ params }) => {
    if (!/^\d+$/.test(params.id)) throw notFound();
  },
  component: PurchaseOrderDetail,
});

/**
 * Only these can still be received; the API rejects the rest with a 400. TASK-0247: a received order
 * closed short (INC-4) also takes a late backorder (`receivableOrder`).
 */
const RECEIVABLE = new Set(['draft', 'submitted', 'partially_received']);
function receivableOrder(po: Pick<PurchaseOrder, 'status' | 'closedShortAt'>): boolean {
  return (
    RECEIVABLE.has(po.status) || (po.status === 'received' && po.closedShortAt != null)
  );
}
/** `reverse` refuses anything that has not actually taken stock in. */
const REVERSIBLE = new Set(['received', 'partially_received']);
/** TASK-0236: an invoice can be recorded on a sent, partly received or received PO. */
const INVOICEABLE = new Set(['submitted', 'partially_received', 'received']);
/** TASK-0252: invoice costs are entered on a partly or fully received order (closed short included). */
const COSTABLE = new Set(['partially_received', 'received']);

function PurchaseOrderDetail() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const mode: Mode = search.mode ?? 'lines';
  const navigate = useNavigate({ from: Route.fullPath });
  const poId = Number(id);
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: poKeys.detail(poId),
    queryFn: () => api.get<PurchaseOrder>(`/vendors/purchase-orders/${poId}`),
  });

  const [reverseOpen, setReverseOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  // TASK-0251 (INC-4): Close short, and Cancel through the dialog that reads the order first (review M1).
  const [closeShortOpen, setCloseShortOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState('');
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [statusAction, setStatusAction] = useState<null | {
    status: 'draft';
    title: string;
    confirmLabel: string;
    consequences: string[];
  }>(null);

  const [markOpen, setMarkOpen] = useState(false);
  // TASK-0256 (DROP-1): Change delivery (another drop-off location or direct, re-planned days).
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const locationList = useDropOffLocations();
  const hasLocations = (locationList.data ?? []).some((l) => l.active);
  // Review F2: a draft with no pick of its own goes where the vendor usually delivers when it is ordered.
  const followsVendor =
    detail.data?.status === 'draft' && detail.data.shipToChosen === false;
  const [markError, setMarkError] = useState<string | null>(null);
  // TASK-0254 (VO-2): Mark as ordered here keeps a copy of the order as prepared (the original dispatch).
  const orderFormat = useVendorOrderFormat(detail.data?.vendorId);
  const storeName = useStoreName();
  const setStatus = useMutation({
    mutationFn: (change: 'draft' | ({ status: 'submitted' } & MarkOrderedInput)) =>
      api.put<PurchaseOrder>(
        `/vendors/purchase-orders/${poId}`,
        typeof change === 'string' ? { status: change } : change
      ),
    onSuccess: (updated, change) => {
      if (
        typeof change !== 'string' &&
        updated.status === 'submitted' &&
        orderFormat.data &&
        // A draft that went back from ordered already has its copy (review G3): Send again is the way on.
        (queryClient.getQueryData<readonly unknown[]>(dispatchKeys.po(poId))?.length ??
          0) === 0
      ) {
        // Best effort, after the record: the PO is ordered either way, and the Order sent card offers
        // "Keep a copy of this order" when this did not go through.
        void keepOriginalCopy({
          po: updated,
          settings: orderFormat.data.settings,
          storeName,
          placedBy: change.placedBy,
          sentVia: change.sentVia,
        }).then(() => queryClient.invalidateQueries({ queryKey: dispatchKeys.po(poId) }));
      }
      queryClient.setQueryData(poKeys.detail(poId), updated);
      void queryClient.invalidateQueries({ queryKey: poKeys.all });
      setStatusAction(null);
      setMarkOpen(false);
      setMarkError(null);
      toast.success(
        updated.status === 'submitted'
          ? `PO #${poId} is marked as ordered.`
          : `PO #${poId} is now ${poStatusLabel(updated.status).toLowerCase()}.`
      );
    },
    onError: (error) => {
      if (markOpen) setMarkError(errorMessage(error));
      else toast.error(errorMessage(error));
    },
  });

  // TASK-0230: what the reverse will take out of stock, per item, from the same plan the API runs.
  const reversePreview = useQuery({
    queryKey: [...poKeys.detail(poId), 'reverse-preview'],
    queryFn: () =>
      api.get<ReversePreview>(`/vendors/purchase-orders/${poId}/reverse-preview`),
    enabled: reverseOpen,
    staleTime: 0,
  });

  const reverse = useMutation({
    mutationFn: (reason: string) =>
      api.post<PurchaseOrder>(`/vendors/purchase-orders/${poId}/reverse`, { reason }),
    onSuccess: (updated) => {
      queryClient.setQueryData(poKeys.detail(poId), updated);
      invalidateAfterReceive(queryClient);
      setReverseOpen(false);
      setReverseReason('');
      setReverseError(null);
      const previewLines = reversePreview.data?.lines ?? [];
      const removed = previewLines.reduce((sum, line) => sum + line.removedFromStock, 0);
      // Pounds are never added to eaches: the units come from the order's own lines.
      const removedTotals = reverseTotals(previewLines, updated.items ?? []);
      const now = updated.status === 'cancelled' ? 'cancelled' : 'back to ordered';
      toast.success(
        removed > 0
          ? `Receipt reversed. PO #${poId} is ${now} and ${unitTotalsText(
              removedTotals
            )} came back out of stock.`
          : `Receipt reversed. PO #${poId} is ${now}; stock stays as counted.`
      );
    },
    onError: (error) => {
      // 409 means goods have already been sold — the server's sentence names
      // the product and the projected balance, so it is shown verbatim.
      setReverseError(errorMessage(error));
    },
  });

  if (detail.isPending) {
    return (
      <Page>
        <PageHeader eyebrow="Purchasing" title={`PO #${poId}`} />
        <PageBody>
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </PageBody>
      </Page>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <Page>
        <PageHeader
          eyebrow="Purchasing"
          title={`PO #${poId}`}
          actions={
            <Button size="sm" asChild icon={<ArrowLeft className="size-3.5" />}>
              <Link to="/purchasing" search={{}}>
                Back
              </Link>
            </Button>
          }
        />
        <PageBody>
          <ErrorState
            title="Could not load this purchase order"
            message={errorMessage(detail.error)}
            onRetry={() => void detail.refetch()}
          />
        </PageBody>
      </Page>
    );
  }

  const po = detail.data;
  const items = po.items ?? [];
  const receivable = receivableOrder(po);
  const reversible = REVERSIBLE.has(po.status);
  // Pounds are never added to eaches: each total is kept per unit.
  const receivedTotals = totalsByUnit(items, (item) => ({
    unit: item.unit,
    quantity: n(item.receivedQty),
  }));
  const orderedTotals = totalsByUnit(items, (item) => ({
    unit: item.unit,
    quantity: n(item.orderedQty),
  }));
  const anyIn = items.some((item) => n(item.receivedQty) > 0);
  const allIn =
    items.length > 0 && items.every((item) => n(item.receivedQty) >= n(item.orderedQty));
  // TASK-0236: the invoices recorded on this PO, and whether one can be recorded here.
  const invoices = po.invoices ?? [];
  const liveInvoices = invoices.filter((invoice) => invoice.voidedAt == null);
  const madeFromInvoice = po.mintedFromImportId != null;
  const invoiceable =
    INVOICEABLE.has(po.status) &&
    !madeFromInvoice &&
    po.legacyVtransId == null &&
    po.vendorId != null;
  const billing = billingSummary(po);
  const showInvoicesTab = po.status !== 'draft';
  // TASK-0252: the paper-invoice path for door-received orders; and how many received lines have no cost.
  const costable = COSTABLE.has(po.status) && po.legacyVtransId == null;
  const costMissing = items.filter(
    (item) => item.productId != null && n(item.receivedQty) > 0 && item.unitCost == null
  ).length;
  // TASK-0241: an invoice-mode vendor's sent order is checked in from its invoice, not on the phone.
  const invoiceVendorOrder =
    invoiceable &&
    po.vendorReceivingMode === 'invoice' &&
    (po.status === 'submitted' || po.status === 'partially_received');

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link to="/purchasing" search={{}} className="hover:text-ink">
            Purchasing
          </Link>
        }
        title={
          <span className="flex items-center gap-3">
            <span className="font-mono">PO #{po.id}</span>
            <PoStatusBadge status={po.status} closedShort={po.closedShortAt != null} />
          </span>
        }
        subtitle={
          <>
            {po.vendorId ? (
              <Link
                to="/vendors/$id"
                params={{ id: String(po.vendorId) }}
                className="hover:text-ink"
              >
                {po.vendorName ?? `Vendor #${po.vendorId}`}
              </Link>
            ) : (
              'Unassigned vendor'
            )}
            {billing ? (
              <> · {billing}</>
            ) : po.invoiceNumber ? (
              <> · invoice {po.invoiceNumber}</>
            ) : null}
          </>
        }
        tabs={
          <SubNav>
            <SubNavItem
              active={mode === 'lines'}
              onClick={() => void navigate({ search: { mode: 'lines' } })}
            >
              Lines &amp; header
            </SubNavItem>
            {receivable ? (
              <SubNavItem
                active={mode === 'receive'}
                onClick={() => void navigate({ search: { mode: 'receive' } })}
              >
                Receive
              </SubNavItem>
            ) : null}
            {costable ? (
              <SubNavItem
                active={mode === 'costs'}
                onClick={() => void navigate({ search: { mode: 'costs' } })}
              >
                <span className="inline-flex items-center gap-1.5">
                  Invoice costs
                  {costMissing > 0 ? <Count value={costMissing} tone="warn" /> : null}
                </span>
              </SubNavItem>
            ) : null}
            {showInvoicesTab ? (
              <SubNavItem
                active={mode === 'invoices'}
                onClick={() => void navigate({ search: { mode: 'invoices' } })}
              >
                <span className="inline-flex items-center gap-1.5">
                  Invoices
                  <Count value={liveInvoices.length + (po.importsAimed?.length ?? 0)} />
                </span>
              </SubNavItem>
            ) : null}
          </SubNav>
        }
        actions={
          <>
            {invoiceable ? (
              <>
                <Button
                  size="sm"
                  icon={<ScanLine className="size-3.5" />}
                  onClick={() => setScanOpen(true)}
                >
                  Scan invoice
                </Button>
                <Button size="sm" icon={<Keyboard className="size-3.5" />} asChild>
                  <Link
                    to="/purchasing/$id/invoice"
                    params={{ id: String(po.id) }}
                    search={{ mode: 'manual' }}
                  >
                    Enter invoice
                  </Link>
                </Button>
              </>
            ) : po.status === 'draft' ? (
              <span
                className="text-2xs text-ink-faint inline-flex items-center gap-1"
                title="An invoice is recorded on an order that was sent"
              >
                <FileText className="size-3" /> Mark as ordered first
              </span>
            ) : null}
            {po.status === 'draft' ? (
              <Button
                size="sm"
                icon={<Send className="size-3.5" />}
                loading={setStatus.isPending}
                title="Records that this order has been placed with the vendor. Nothing is sent from here and no stock or cost moves."
                onClick={() => {
                  setMarkError(null);
                  setMarkOpen(true);
                }}
              >
                Mark as ordered
              </Button>
            ) : null}
            {po.status === 'submitted' && liveInvoices.length === 0 ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setStatusAction({
                    status: 'draft',
                    title: `Return PO #${po.id} to draft?`,
                    confirmLabel: 'Return to draft',
                    consequences: ['Lets you edit the line set again.'],
                  })
                }
              >
                Back to draft
              </Button>
            ) : null}
            {/* TASK-0251 (INC-4): something came and a line is still short: close it short. */}
            {po.canCloseShort ? (
              <Button
                size="sm"
                icon={<PackageX className="size-3.5" />}
                title="The missing lines will not come: keep the order as received with what did come."
                onClick={() => setCloseShortOpen(true)}
              >
                Close short
              </Button>
            ) : null}
            {/* TASK-0251 (INC-4): Cancel only while nothing was received; a partly received order is closed short.
                Review M1: the dialog reads the order again and the cancel is refused if something came since. */}
            {RECEIVABLE.has(po.status) && liveInvoices.length === 0 && !anyIn ? (
              <Button
                size="sm"
                variant="danger"
                icon={<Ban className="size-3.5" />}
                onClick={() => setCancelOpen(true)}
              >
                Cancel order
              </Button>
            ) : null}
            {/* The API refuses to reopen a cancelled PO that took stock in
                (TASK-0226): as a draft its lines could be re-keyed and received twice. */}
            {po.status === 'cancelled' && !anyIn && !madeFromInvoice ? (
              <Button
                size="sm"
                onClick={() =>
                  setStatusAction({
                    status: 'draft',
                    title: `Reopen PO #${po.id}?`,
                    confirmLabel: 'Reopen as draft',
                    consequences: ['Puts the order back in draft so it can be edited.'],
                  })
                }
              >
                Reopen
              </Button>
            ) : null}
            {reversible ? (
              <Button
                size="sm"
                variant="danger"
                icon={<Undo2 className="size-3.5" />}
                onClick={() => {
                  setReverseError(null);
                  setReverseOpen(true);
                }}
              >
                Reverse receipt
              </Button>
            ) : null}
          </>
        }
      />

      <PageBody>
        <StatRow className="mb-4 md:grid-cols-4 xl:grid-cols-4">
          <Stat label="Order total" value={money(po.total ?? 0)} />
          <Stat label="Lines" value={num(items.length)} />
          <Stat
            label="Received total"
            value={unitTotalsText(receivedTotals, '0')}
            sub={`of ${unitTotalsText(orderedTotals, '0')} ordered`}
            tone={allIn ? 'positive' : anyIn ? 'warn' : undefined}
          />
          <Stat
            label={po.shipTo ? 'Expected at the store' : 'Expected'}
            value={expectedLabel(po.expectedAt)}
            sub={
              po.receivedAt
                ? `received ${smartDate(po.receivedAt)}`
                : po.overdue
                  ? 'late'
                  : // TASK-0256 (DROP-1): the drop day at the drop-off location.
                    (poDeliveryText(po).via ?? undefined)
            }
            tone={po.overdue && !po.receivedAt ? 'warn' : undefined}
          />
        </StatRow>
        {DELIVERY_OPEN.has(po.status) && (po.shipTo || hasLocations) ? (
          <div
            className="text-2xs text-ink-muted mb-3 flex items-center gap-2"
            data-testid="po-deliver-to"
          >
            <span>
              Deliver to:{' '}
              <span className="text-ink">
                {followsVendor
                  ? `${(locationList.data ?? []).find((l) => l.id === startLocationOf(po))?.name ?? DIRECT_LABEL} (the vendor's usual)`
                  : po.shipTo
                    ? po.shipTo.name
                    : DIRECT_LABEL}
              </span>
              {po.shipTo && !followsVendor
                ? ` · ${po.dropOn ? `drops ${weekdayDayLabel(po.dropOn)}` : 'no drop day'}${po.expectedOn ? ` · at the store ${weekdayDayLabel(po.expectedOn)}` : ''}`
                : ''}
            </span>
            <Button size="xs" variant="ghost" onClick={() => setDeliveryOpen(true)}>
              Change delivery
            </Button>
          </div>
        ) : null}

        {po.closedShortAt ? (
          <Callout
            tone="neutral"
            className="mb-4"
            title={closedShortSummary(po, smartDate(po.closedShortAt))}
          >
            {(po.shortLines?.length ?? 0) > 0
              ? `${countLabel(po.shortLines!.length, 'line')} did not come in full and no longer count as on order. Stock already received stays. If the rest turns up, receive it on the Receive tab.`
              : 'Every line has come in since.'}
          </Callout>
        ) : null}
        {invoiceVendorOrder ? (
          <Callout tone="info" className="mb-4">
            This vendor is checked in from the invoice first: use Scan invoice when it
            comes. The floor phone can check in an order that is not fully invoiced yet,
            and the invoice is matched to that count.
          </Callout>
        ) : null}
        {po.lastReceivedAt ? (
          <p className="text-2xs text-ink-muted mb-3" data-testid="last-received">
            Last received {smartDate(po.lastReceivedAt)}
            {po.lastReceivedByName ? ` by ${po.lastReceivedByName}` : ''}
            {po.lastReceivedDeviceName
              ? ` on the phone “${po.lastReceivedDeviceName}”`
              : ''}
          </p>
        ) : null}
        {mode !== 'costs' && costable && costMissing > 0 ? (
          <Callout
            tone="warn"
            className="mb-4"
            title={`${countLabel(costMissing, 'received line')} ${costMissing === 1 ? 'has' : 'have'} no cost yet`}
            actions={
              <Button
                size="xs"
                variant="primary"
                onClick={() => void navigate({ search: { mode: 'costs' } })}
              >
                Enter invoice costs
              </Button>
            }
          >
            The stock is in. Enter the costs from the vendor's invoice; an order estimate
            is never used as a cost.
          </Callout>
        ) : null}
        {mode === 'receive' && receivable ? (
          <ReceivePanel po={po} />
        ) : mode === 'costs' && costable ? (
          <EnterCostsPanel po={po} />
        ) : mode === 'invoices' && showInvoicesTab ? (
          <InvoicesTab
            po={po}
            selectedId={search.invoice ?? null}
            onSelect={(invoice) =>
              void navigate({
                search: { mode: 'invoices', ...(invoice != null ? { invoice } : {}) },
              })
            }
          />
        ) : (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="min-w-0 space-y-4">
              <LinesPanel po={po} />
              {/* Every status (review G3): a draft that went back from ordered keeps its history. */}
              <OrderSentPanel po={po}>
                <OrderTextPanel po={po} />
              </OrderSentPanel>
            </div>
            <div className="min-w-0 space-y-4">
              <HeaderPanel po={po} />
              <PoFreightPanel poId={po.id} />
            </div>
          </div>
        )}
      </PageBody>

      {statusAction ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setStatusAction(null);
          }}
          title={statusAction.title}
          confirmLabel={statusAction.confirmLabel}
          variant="primary"
          consequences={statusAction.consequences}
          loading={setStatus.isPending}
          onConfirm={() => setStatus.mutate(statusAction.status)}
        />
      ) : null}

      <ConfirmDialog
        open={reverseOpen}
        onOpenChange={(open) => {
          setReverseOpen(open);
          if (!open) {
            setReverseReason('');
            setReverseError(null);
          }
        }}
        title={`Reverse the receipt on PO #${po.id}?`}
        description="Use this when an invoice was entered wrong and needs re-keying."
        confirmLabel="Reverse receipt"
        typeToConfirm="REVERSE"
        loading={reverse.isPending}
        consequences={[
          reversePreview.data
            ? `Takes ${unitTotalsText(
                reverseTotals(reversePreview.data.lines, items),
                'nothing'
              )} back out of stock: only what this order added after each item was last counted.`
            : 'Takes back out of stock only what this order added after each item was last counted.',
          'Voids the advisory lot rows this receipt created.',
          ...reverseInvoiceConsequences(reversePreview.data),
          ...reverseCostConsequences(reversePreview.data),
          'Refused if any of the stock has already been sold.',
        ]}
        onConfirm={() => {
          const reason = reverseReason.trim();
          if (reason.length < 4) {
            setReverseError(
              'Give a reason of at least 4 characters — it is written onto the inventory rows.'
            );
            return;
          }
          reverse.mutate(reason);
        }}
      >
        <ReversePreviewList preview={reversePreview} />
        <Field
          label="Reason"
          required
          hint="4–500 characters. Stored on every reversing inventory adjustment."
          error={reverseError}
        >
          <Textarea
            rows={3}
            value={reverseReason}
            maxLength={500}
            invalid={!!reverseError}
            placeholder="Invoice was keyed against the wrong PO…"
            onChange={(e) => {
              setReverseReason(e.currentTarget.value);
              setReverseError(null);
            }}
          />
        </Field>
      </ConfirmDialog>

      {scanOpen ? (
        <ScanInvoiceDialog open onOpenChange={setScanOpen} poId={po.id} />
      ) : null}

      <CloseShortDialog
        poId={po.id}
        open={closeShortOpen}
        onOpenChange={setCloseShortOpen}
        onClosed={() => void navigate({ search: { mode: 'lines' } })}
      />

      <CancelOrderDialog
        poId={po.id}
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onCloseShort={() => {
          setCancelOpen(false);
          setCloseShortOpen(true);
        }}
      />

      <MarkOrderedDialog
        po={po}
        open={markOpen}
        loading={setStatus.isPending}
        error={markError}
        onOpenChange={(open) => {
          setMarkOpen(open);
          if (!open) setMarkError(null);
        }}
        onConfirm={(input) => setStatus.mutate({ status: 'submitted', ...input })}
      />
      <ChangeDeliveryDialog po={po} open={deliveryOpen} onOpenChange={setDeliveryOpen} />
    </Page>
  );
}

/** TASK-0252: what the reverse does to cost changes waiting for approval, and to costs already applied. */
function reverseCostConsequences(preview: ReversePreview | undefined): string[] {
  if (!preview) return [];
  const out: string[] = [];
  const waiting = preview.withdrawsCostChanges ?? 0;
  if (waiting > 0)
    out.push(
      `Withdraws ${countLabel(waiting, 'cost change')} waiting for approval (rejected: receipt reversed).`
    );
  const kept = preview.keepsAppliedCosts ?? 0;
  if (kept > 0)
    out.push(
      `${countLabel(kept, 'item cost')} this order set ${kept === 1 ? 'stays' : 'stay'} as ${kept === 1 ? 'it is' : 'they are'}: check them under Costs if they were wrong.`
    );
  return out;
}

/**
 * TASK-0236: what the reverse does to the PO's invoices and its status, in the dialog's words.
 * Before the preview answers, the PO's own copy says whether it was made from an invoice.
 */
function reverseInvoiceConsequences(preview: ReversePreview | undefined): string[] {
  if (!preview)
    return ['Puts the order back to ordered, with every received quantity back at zero.'];
  const out: string[] = [];
  if (preview.cancelsMintedOrder) {
    out.push(
      preview.importStaysClosed
        ? 'This PO was made from part of an invoice whose other lines were held back: reversing cancels it, and the invoice does not go back to the inbox. Enter the corrected lines with Enter invoice or Record invoice.'
        : 'This PO was made from an invoice: reversing cancels it and returns the invoice to the inbox.'
    );
  } else {
    out.push(
      'Puts the order back to ordered, with every received quantity back at zero.'
    );
  }
  for (const invoice of preview.releasesInvoices ?? []) {
    out.push(
      `Also voids invoice ${invoice.number ?? '(no number)'}; its scan, if any, goes back to the inbox.`
    );
  }
  return out;
}

/* ── Reverse preview ───────────────────────────────────────────────────────── */

/**
 * Per item, what the reverse does to stock (TASK-0230, D-07): stock this order added after the
 * item's last count comes back out; anything the count already settled stays as counted.
 */
function ReversePreviewList({
  preview,
}: {
  preview: {
    data?: ReversePreview;
    isPending: boolean;
    isError: boolean;
    error: unknown;
  };
}) {
  if (preview.isPending) return <Skeleton className="mb-3 h-16 w-full" />;
  if (preview.isError || !preview.data) {
    return (
      <p className="text-2xs text-negative mb-3">
        Could not load what the reverse would remove: {errorMessage(preview.error)}
      </p>
    );
  }
  if (preview.data.lines.length === 0) return null;
  return (
    <ul
      className="border-line bg-surface-sunken text-2xs mb-3 max-h-56 space-y-1.5 overflow-y-auto rounded border p-2"
      aria-label="What the reverse does to stock"
    >
      {preview.data.lines.map((line) => {
        let text: string;
        if (line.removedFromStock > 0) {
          text =
            line.removedFromStock > line.onHand
              ? `Will remove ${qty(line.removedFromStock)} from stock, but only ${qty(line.onHand)} is on hand — the reverse will be refused`
              : `Will remove ${qty(line.removedFromStock)} from stock`;
        } else if (line.notTracked) {
          text = 'Nothing to take out (this item is not tracked)';
        } else if (line.keptBecauseCountedOn) {
          text = `Stock stays as counted on ${storeDayLabel(line.keptBecauseCountedOn)} (counted after this delivery)`;
        } else {
          text = 'Nothing to take out (this delivery did not add stock)';
        }
        return (
          <li key={line.productId} className="min-w-0">
            <div className="text-ink truncate">{line.name}</div>
            <div
              className={
                line.removedFromStock > line.onHand ? 'text-negative' : 'text-ink-muted'
              }
            >
              {text}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** What a reverse takes out of stock, per unit (FR-WORD), from the reverse preview (TASK-0230). */
function reverseTotals(
  lines: readonly ReversePreviewLine[],
  items: readonly Pick<PurchaseOrderItem, 'productId' | 'unit'>[]
) {
  return totalsByUnit(lines, (line) => ({
    unit: items.find((item) => item.productId === line.productId)?.unit,
    quantity: line.removedFromStock,
  }));
}

/* ── Header edit ───────────────────────────────────────────────────────────── */

function HeaderPanel({ po }: { po: PurchaseOrder }) {
  const queryClient = useQueryClient();
  // The stored day is read with UTC date parts and sent back as the same plain
  // date, so saving the header never moves the expected day.
  const [expectedAt, setExpectedAt] = useState(expectedOnFromIso(po.expectedAt));
  const [notes, setNotes] = useState(po.notes ?? '');

  useEffect(() => {
    setExpectedAt(expectedOnFromIso(po.expectedAt));
    setNotes(po.notes ?? '');
  }, [po.id, po.expectedAt, po.notes]);

  const dirty =
    expectedAt !== expectedOnFromIso(po.expectedAt) || notes !== (po.notes ?? '');

  const save = useMutation({
    mutationFn: () => {
      // A cleared date must go as an explicit null; omitting it would leave
      // the old value in place. A typed one goes as the same plain date.
      const expected = expectedAt === '' ? null : isoFromExpectedOn(expectedAt);
      if (expectedAt !== '' && expected === null) {
        throw new Error('The expected date is not a real day. Nothing was saved.');
      }
      return api.put<PurchaseOrder>(`/vendors/purchase-orders/${po.id}`, {
        expectedAt: expected,
        notes,
      });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(poKeys.detail(po.id), updated);
      void queryClient.invalidateQueries({ queryKey: poKeys.all });
      toast.success('Header saved.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Panel
      title="Header"
      footer={
        dirty ? (
          <div className="flex items-center justify-between gap-2">
            <span>Unsaved header changes</span>
            <Button
              size="xs"
              variant="primary"
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Save header
            </Button>
          </div>
        ) : (
          'Saved'
        )
      }
    >
      <div className="space-y-3">
        <Field
          label={po.shipTo ? 'Expected at the store' : 'Expected'}
          hint={
            po.shipTo
              ? `Keeps ${po.shipTo.name} and its drop day; use Change delivery to change those.`
              : 'Clear the field to remove the expected date entirely.'
          }
          action={
            expectedAt ? (
              <button
                onClick={() => setExpectedAt('')}
                className="text-2xs text-signal-ink hover:underline"
              >
                Clear
              </button>
            ) : null
          }
        >
          <Input
            type="date"
            size="sm"
            mono
            value={expectedAt}
            onChange={(e) => setExpectedAt(e.currentTarget.value)}
          />
        </Field>
        <Field label="Notes">
          <Textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
          />
        </Field>
        <DetailList>
          <Detail label="Origin">{originLabel(po)}</Detail>
          <Detail label="Created" mono>
            {po.createdAt ? smartDate(po.createdAt) : '—'}
          </Detail>
          <Detail label="Marked ordered" mono>
            {po.submittedAt ? smartDate(po.submittedAt) : '—'}
          </Detail>
          {po.placedBy ? <Detail label="Placed by">{po.placedBy}</Detail> : null}
          {po.sentVia || po.submittedByName ? (
            <Detail label="Sent">
              {[
                channelLabel(po.sentVia) ?? null,
                po.submittedByName ? `by ${po.submittedByName}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Detail>
          ) : null}

          {po.deliveredOn ? (
            <Detail label="Arrived" mono>
              {storeDayLabel(po.deliveredOn)}
            </Detail>
          ) : null}
          {po.legacyVtransId ? (
            <Detail label="Legacy PO" mono>
              <Code copyable>{po.legacyVtransId}</Code>
            </Detail>
          ) : null}
        </DetailList>
      </div>
    </Panel>
  );
}

/** TASK-0241: where the order came from, in plain words. */
function originLabel(po: PurchaseOrder): string {
  const origin = po.origin ?? (po.mintedFromImportId != null ? 'invoice' : null);
  if (origin === 'order_guide') return 'Order guide';
  if (origin === 'invoice') return 'Made from an invoice';
  return 'Made by hand';
}

/**
 * TASK-0241: the order as the vendor gets it, with the hand-off buttons, once it is marked as ordered
 * (record first, then hand off). The text comes from the PO's own lines and pack snapshot.
 *
 * TASK-0253 (VO-1): in the vendor's order format (Email list, File attachment, CSV in the email), from
 * the same renderer as Send. TASK-0254 (VO-2): shown inside the Order sent card only while no copy of
 * the order was kept (the card then offers to keep one); once kept, the card shows the stored order.
 */
function OrderTextPanel({ po }: { po: PurchaseOrder }) {
  const storeName = useStoreName();
  const format = useVendorOrderFormat(po.vendorId);
  const settings = format.data?.settings ?? null;
  const order =
    settings === null
      ? null
      : renderVendorOrder(
          poOrderLines(po),
          settings,
          poOrderContext(
            po,
            storeName,
            orderFallbackEmail(po.vendorOrderContact, po.vendorEmail)
          )
        );
  const vendorName = po.vendorName ?? 'the vendor';
  return (
    <Panel
      title="Order text"
      description={
        order?.format === 'csv_file'
          ? 'What the vendor gets: download the file, then email it.'
          : 'What the vendor gets: copy, WhatsApp, email or print it.'
      }
    >
      {format.isError ? (
        <p className="text-2xs text-negative">
          Could not load how {vendorName} takes orders: {errorMessage(format.error)}
        </p>
      ) : order === null ? (
        <p className="text-2xs text-ink-faint" aria-busy="true">
          Preparing the order…
        </p>
      ) : (
        <div className="space-y-3">
          {order.format === 'csv_file' ? <OrderTable order={order} maxRows={6} /> : null}
          <UnplacedPanel order={order} vendorName={vendorName} />
          <OrderOutputPanel
            order={order}
            channel={po.sentVia ?? null}
            whatsAppNumber={orderFallbackPhone(po.vendorOrderContact, po.vendorPhone)}
            rows={8}
          />
        </div>
      )}
    </Panel>
  );
}

/** `2 cs × 12` for a line ordered in cases (its own snapshot), else the quantity. */
function orderedText(item: PurchaseOrderItem): string {
  const packs = item.orderedPacks == null ? null : n(item.orderedPacks);
  if (item.unit !== 'lb' && packs !== null && item.orderPackSize != null) {
    return `${qty(packs)} cs × ${item.orderPackSize}`;
  }
  return qty(n(item.orderedQty));
}

/* ── Line editing ──────────────────────────────────────────────────────────── */

type DraftLine = {
  /** Local row key; PO item ids are not stable across a replacement save. */
  key: string;
  productId: number;
  productName: string;
  /** Raw text of the quantity box; read once more when the request is built. */
  orderedQtyText: string;
  /** TASK-0241: the cost box. An estimate (order price) unless the PO's costs are invoice costs. */
  unitCostCents: number | null;
  /** The line's real cost before editing (null: none, or a new line). Kept when the box is unchanged. */
  realUnitCostCents: number | null;
  unit: 'ea' | 'lb';
  /** TASK-0247 review N2: the line was added at the door ("Add item to this delivery", ordered 0). */
  addedAtDoor?: boolean;
};

/**
 * Why a draft line's quantity cannot be saved as zero. Review N2: a line added at the door stays at
 * ordered 0, and the order's line set only takes quantities above zero, so say what to do with it
 * instead of only "above zero". Review R2-2: never "receive it" first: a received line ends line
 * editing (Edit lines goes away and the API refuses a new line set), so the only way on is to remove
 * it and add it again on the Receive tab when it arrives.
 */
function zeroQuantityProblem(line: DraftLine): string {
  return line.addedAtDoor
    ? `${line.productName} was added at the door with nothing ordered. Remove it to change the other lines, then add it again with Add item to this delivery when it is received.`
    : `${line.productName}: quantity must be above zero.`;
}

function LinesPanel({ po }: { po: PurchaseOrder }) {
  const queryClient = useQueryClient();
  const items = useMemo(() => po.items ?? [], [po.items]);
  const [editing, setEditing] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const anyReceived = items.some((item) => n(item.receivedQty) > 0);
  /**
   * `PUT …/:id` replaces the whole line set. Since TASK-0226 it keeps each
   * line's unit and shipped case/weight fields, and refuses the edit itself
   * once anything is received or the PO is past submitted (the rule this flag
   * mirrors). Weight-billed (lb) lines are edited here too since TASK-0228:
   * the quantity box takes a typed decimal.
   */
  // TASK-0247 review F2: the API refuses the line set while an invoice is live on the order (its billed
  // cost sits on the lines), as it refuses status changes; void the invoice first.
  const hasLiveInvoice = (po.invoices ?? []).some((invoice) => invoice.voidedAt == null);
  // TASK-0253 review R2-1: an imported line with no product cannot be saved back as a draft line.
  const unmatchedLine = items.some((item) => item.productId === null);
  const editable =
    !anyReceived &&
    !hasLiveInvoice &&
    !unmatchedLine &&
    (po.status === 'draft' || po.status === 'submitted');
  const invoiceCosts = po.mintedFromImportId != null || po.origin === 'invoice';
  const costLabel = invoiceCosts ? 'Unit cost' : 'Est. cost';
  // TASK-0257 (DROP-2a): cost with freight beside cost, only when freight reaches this order.
  const landed = usePoLanded(po.id).data;
  const [showFreight, setShowFreight] = useCostWithFreightToggle();
  const freightOn = landed?.applies === true && showFreight;
  const landedLine = (id: number) => landed?.lines.find((l) => l.poItemId === id);

  const startEditing = () => {
    setLines(
      items.flatMap((item, index) =>
        item.productId === null
          ? [] // never: editable is false with an unmatched line
          : {
              key: `${item.id}-${index}`,
              productId: item.productId,
              productName: poItemName(item),
              orderedQtyText: quantityText(n(item.orderedQty)),
              unitCostCents: item.unitCost ?? item.orderedUnitCost ?? null,
              realUnitCostCents: item.unitCost ?? null,
              unit: (item.unit as 'ea' | 'lb' | null) ?? 'ea',
              addedAtDoor: !(n(item.orderedQty) > 0),
            }
      )
    );
    setEditing(true);
  };

  const save = useMutation({
    mutationFn: () =>
      api.put<PurchaseOrder>(`/vendors/purchase-orders/${po.id}`, {
        // The one conversion from typed text; throws rather than send a
        // blank or unreadable box.
        items: lines.map((line) => {
          const orderedQty = quantityForRequest(
            line.orderedQtyText,
            line.unit,
            line.productName
          );
          if (orderedQty <= 0) {
            throw new Error(zeroQuantityProblem(line));
          }
          // TASK-0241: a typed or prefilled cost is an order price (never product cost); a PO made
          // from an invoice keeps its invoice costs, and an unchanged real cost stays as it was.
          const cost = invoiceCosts
            ? { unitCost: line.unitCostCents ?? null }
            : line.realUnitCostCents != null &&
                line.unitCostCents === line.realUnitCostCents
              ? { unitCost: line.realUnitCostCents }
              : { orderedUnitCost: line.unitCostCents ?? null };
          return {
            productId: line.productId,
            orderedQty,
            ...cost,
            unit: line.unit,
          };
        }),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(poKeys.detail(po.id), updated);
      void queryClient.invalidateQueries({ queryKey: poKeys.all });
      setEditing(false);
      setConfirmOpen(false);
      toast.success(
        `Line set replaced — ${countLabel(updated.items?.length ?? 0, 'line')}.`
      );
    },
    onError: (error) => {
      setConfirmOpen(false);
      toast.error(errorMessage(error));
    },
  });

  const checks = lines.map((line) => checkQuantity(line.orderedQtyText, line.unit));
  /** Null while the line's box does not read: shown as a dash, adds nothing. */
  const lineTotal = (index: number): number | null => {
    const quantity = checks[index]?.quantity ?? null;
    return quantity === null
      ? null
      : Math.round(quantity * (lines[index]?.unitCostCents ?? 0));
  };
  const draftTotal = lines.reduce((sum, _, i) => sum + (lineTotal(i) ?? 0), 0);
  const lineProblems = lines.map((line, i) => {
    const check = checks[i]!;
    if (check.problem !== null) return `${line.productName}: quantity ${check.problem}.`;
    if (check.quantity <= 0) return zeroQuantityProblem(line);
    return null;
  });
  const problems = lineProblems.filter((problem) => problem !== null);

  if (!editing) {
    return (
      <Panel
        title={`Lines · ${countLabel(items.length, 'line')}`}
        actions={
          <>
            {landed?.applies ? (
              <CostWithFreightSwitch checked={showFreight} onChange={setShowFreight} />
            ) : null}
            {editable ? (
              <Button
                size="xs"
                icon={<Pencil className="size-3" />}
                onClick={startEditing}
              >
                Edit lines
              </Button>
            ) : hasLiveInvoice &&
              !anyReceived &&
              (po.status === 'draft' || po.status === 'submitted') ? (
              <span
                className="text-2xs text-ink-muted"
                data-testid="lines-locked-by-invoice"
              >
                Invoice recorded: void it to change lines
              </span>
            ) : null}
          </>
        }
        bleed
      >
        {items.length === 0 ? (
          <div className="text-ink-faint p-4 text-xs">
            This purchase order has no lines.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-line bg-surface-sunken text-2xs text-ink-faint border-b tracking-wider uppercase">
                <th className="px-3 py-1.5 text-left font-medium">Product</th>
                <th className="w-20 px-3 py-1.5 text-right font-medium">Ordered</th>
                <th className="w-20 px-3 py-1.5 text-right font-medium">Received</th>
                <th className="w-24 px-3 py-1.5 text-right font-medium">Unit cost</th>
                <th className="w-24 px-3 py-1.5 text-right font-medium">Line total</th>
                {freightOn ? (
                  <>
                    <th className="w-24 px-3 py-1.5 text-right font-medium">Freight</th>
                    <th
                      className="w-28 px-3 py-1.5 text-right font-medium"
                      title="landed cost: what it cost to get it onto our shelf"
                    >
                      Cost with freight
                    </th>
                  </>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const ordered = n(item.orderedQty);
                const received = n(item.receivedQty);
                return (
                  <tr key={item.id} className="border-line-soft border-b last:border-0">
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-ink truncate">{poItemName(item)}</span>
                        {item.unit === 'lb' ? <Badge tone="info">lb</Badge> : null}
                        {item.expiryRequired ? <Badge tone="warn">expiry</Badge> : null}
                      </div>
                    </td>
                    <td
                      className="px-3 py-1.5 text-right font-mono tabular-nums"
                      title={
                        item.orderPackSize != null
                          ? `${qty(ordered)} ${item.unit ?? 'ea'}`
                          : undefined
                      }
                    >
                      {orderedText(item)}
                    </td>
                    <td
                      className={
                        received >= ordered && ordered > 0
                          ? 'text-positive px-3 py-1.5 text-right font-mono tabular-nums'
                          : received > 0
                            ? 'text-warn px-3 py-1.5 text-right font-mono tabular-nums'
                            : 'text-ink-faint px-3 py-1.5 text-right font-mono tabular-nums'
                      }
                    >
                      {qty(received)}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                      {item.unitCost == null && item.orderedUnitCost != null ? (
                        <span
                          className="text-ink-muted"
                          title="The price the order was placed at. The invoice sets the real cost."
                        >
                          est. {money(item.orderedUnitCost)}
                        </span>
                      ) : (
                        money(item.unitCost)
                      )}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-1.5 text-right font-mono tabular-nums',
                        item.lineTotalIsEstimate && 'text-ink-muted'
                      )}
                      title={
                        item.lineTotalIsEstimate
                          ? 'Estimated: no invoice cost yet'
                          : undefined
                      }
                    >
                      {money(item.lineTotal ?? 0)}
                    </td>
                    {freightOn ? <FreightCells line={landedLine(item.id)} /> : null}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-line bg-surface-sunken border-t">
                <td
                  className="text-2xs text-ink-faint px-3 py-1.5 tracking-wider uppercase"
                  colSpan={4}
                >
                  {po.totalIsEstimate ? 'Order total (estimated)' : 'Order total'}
                </td>
                <td className="text-ink px-3 py-1.5 text-right font-mono tabular-nums">
                  {money(po.total ?? 0)}
                </td>
                {freightOn ? (
                  <>
                    <td
                      className="text-ink px-3 py-1.5 text-right font-mono tabular-nums"
                      data-testid="lines-freight-total"
                    >
                      {money(landed?.totals.freightCents ?? 0)}
                    </td>
                    <td />
                  </>
                ) : null}
              </tr>
            </tfoot>
          </table>
        )}
        {!editable && items.length > 0 ? (
          <p className="border-line text-2xs text-ink-faint border-t px-3 py-2">
            {anyReceived
              ? 'Lines are locked once stock has been received — reverse the receipt to re-key this order.'
              : 'Lines can only be edited while the order is a draft or ordered.'}
          </p>
        ) : null}
      </Panel>
    );
  }

  return (
    <Panel
      title="Edit lines"
      allowOverflow
      description={
        invoiceCosts
          ? 'Saving replaces the entire line set on this order.'
          : 'Saving replaces the entire line set on this order. Costs here are estimates: the invoice sets the real cost.'
      }
      actions={
        <Button size="xs" variant="ghost" onClick={() => setEditing(false)}>
          Discard
        </Button>
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono tabular-nums">
            {countLabel(lines.length, 'line')} · {money(draftTotal)}
          </span>
          <Button
            size="xs"
            variant="primary"
            disabled={lines.length === 0 || problems.length > 0}
            onClick={() => setConfirmOpen(true)}
          >
            Save line set
          </Button>
        </div>
      }
    >
      <div className="space-y-2">
        <ProductPicker
          vendorId={po.vendorId}
          disabledIds={new Set(lines.map((line) => line.productId))}
          placeholder="Add a product to this order…"
          onPick={(product: PickedProduct) => {
            setLines((prev) => [
              ...prev,
              {
                key: `new-${product.id}-${prev.length}`,
                productId: product.id,
                productName: product.name,
                orderedQtyText: '1',
                unitCostCents: product.vendorUnitCost ?? product.cost ?? null,
                realUnitCostCents: null,
                unit: product.unit,
              },
            ]);
          }}
        />

        {lines.length === 0 ? (
          <p className="border-negative/30 bg-negative-muted text-2xs text-negative rounded border p-2">
            A purchase order needs at least one line. Add one, or discard to leave the
            order as it was.
          </p>
        ) : null}

        <div className="divide-line-soft divide-y">
          {lines.map((line, index) => (
            <div key={line.key} className="flex items-center gap-2 py-1.5">
              <span className="text-ink min-w-0 flex-1 truncate text-xs">
                {line.productName}
                {line.unit === 'lb' ? (
                  <Badge tone="info" className="ml-1.5">
                    lb
                  </Badge>
                ) : null}
              </span>
              <QuantityInput
                size="sm"
                className="w-20"
                unit={line.unit}
                value={line.orderedQtyText}
                invalid={lineProblems[index] != null}
                selectOnFocus
                aria-label={`Ordered quantity for ${line.productName}`}
                onValueChange={(text) =>
                  setLines((prev) =>
                    prev.map((l, i) => (i === index ? { ...l, orderedQtyText: text } : l))
                  )
                }
              />
              <MoneyInput
                size="sm"
                className="w-24"
                value={line.unitCostCents}
                aria-label={`${costLabel} for ${line.productName}`}
                onValueChange={(cents) =>
                  setLines((prev) =>
                    prev.map((l, i) => (i === index ? { ...l, unitCostCents: cents } : l))
                  )
                }
              />
              <span className="text-2xs text-ink-muted w-20 shrink-0 text-right font-mono tabular-nums">
                {lineTotal(index) === null ? '—' : money(lineTotal(index))}
              </span>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label={`Remove ${line.productName}`}
                onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>

        {problems.length > 0 ? (
          <ul className="text-2xs text-negative space-y-0.5">
            {problems.map((problem, index) => (
              <li key={index}>{problem}</li>
            ))}
          </ul>
        ) : null}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Replace the line set?"
        confirmLabel="Replace lines"
        variant="primary"
        loading={save.isPending}
        consequences={[
          `The order's existing ${countLabel(items.length, 'line')} are deleted and ${countLabel(
            lines.length,
            'line'
          )} inserted in their place.`,
          `New order total ${money(draftTotal)} (was ${money(po.total ?? 0)}).`,
          'No stock moves — receiving is a separate step.',
        ]}
        onConfirm={() => save.mutate()}
      />
    </Panel>
  );
}

/* ── Receiving ─────────────────────────────────────────────────────────────── */

type ReceiveRow = {
  id: number;
  productId: number | null;
  productName: string;
  unit: 'ea' | 'lb';
  orderedQty: number;
  /** "Already in" from the latest server copy of the order. */
  alreadyReceived: number;
  /**
   * The "Already in" the operator's typed total was entered against, sent as
   * `expectedReceivedQty` (TASK-0230 review F5). A background refetch moves `alreadyReceived`
   * but never this: only the changed-since banner's Reload does, so the server still refuses a
   * line someone else received in the meantime.
   */
  expectedReceived: number;
  orderedUnitCost: number | null;
  currentUnitCost: number | null;
  needsExpiry: boolean;
  /** TASK-0236: billed on this PO's invoices, and the invoice it is billed on (when one). */
  billedQty: number;
  /** TASK-0247: what invoices checked in on this line ("from invoice N"), and what the door has not confirmed. */
  fromInvoices: { number: string | null; qty: number }[];
  /**
   * Raw text of the "Total received after this delivery" box: the absolute cumulative received qty,
   * which is what the API stores. Read once more when the request is built.
   */
  receivedQtyText: string;
  unitCostCents: number | null;
  /**
   * TASK-0241 review R2-3: the operator typed in the cost box. Only then is `unitCost` sent: a
   * cost the screen merely loaded is the line's own (an invoice's billed price, say) and may have
   * changed since, so sending it back would count as typed and reach product cost.
   */
  costEdited: boolean;
  /**
   * The line's cost the cost box was typed against, sent as `expectedUnitCost` with an edited
   * cost: the API refuses the line if an invoice re-priced it since (409 "changed since").
   */
  expectedUnitCost: number | null;
  /** TASK-0252: "this is the unit cost" for a cost 3x away from what the item usually costs. */
  ackUnitCase: boolean;
  expirationDate: string;
  lotNumber: string;
  touched: boolean;
};

function buildRows(items: PurchaseOrderItem[]): ReceiveRow[] {
  return items.map((item) => {
    const already = n(item.receivedQty);
    return {
      id: item.id,
      productId: item.productId ?? null,
      productName: poItemName(item),
      unit: (item.unit as 'ea' | 'lb' | null) ?? 'ea',
      orderedQty: n(item.orderedQty),
      alreadyReceived: already,
      expectedReceived: already,
      orderedUnitCost: item.orderedUnitCost ?? null,
      currentUnitCost: item.unitCost ?? null,
      needsExpiry:
        item.expiryRequired === true ||
        item.storageType === 'frozen' ||
        item.storageType === 'refrigerated',
      billedQty: n(item.billedQty),
      fromInvoices: (item.receivedFromInvoices ?? []).map((r) => ({
        number: r.number,
        qty: r.qty,
      })),
      receivedQtyText: quantityText(already),
      unitCostCents: item.unitCost ?? null,
      costEdited: false,
      expectedUnitCost: item.unitCost ?? null,
      ackUnitCase: false,
      expirationDate: '',
      lotNumber: '',
      touched: false,
    };
  });
}

/** Whether receiving `received` on this row sends anything. */
function rowMoves(row: ReceiveRow, received: number): boolean {
  return (
    received !== row.alreadyReceived ||
    (received > 0 && row.costEdited && row.unitCostCents !== row.currentUnitCost)
  );
}

/** Today for an order sent (submitted) in the last 2 days; otherwise blank, so the date is asked. */
function defaultArrivedOn(po: PurchaseOrder, today: string): string {
  if (!po.submittedAt) return '';
  const sent = Date.parse(po.submittedAt);
  return Number.isFinite(sent) && Date.now() - sent <= 2 * 86_400_000 ? today : '';
}

/**
 * Re-seed from a newer server copy of the order, keeping what the operator typed on rows they
 * touched (TASK-0230): after "changed since you opened it" they reload to see the new "Already in"
 * and reconcile, without retyping the delivery. A touched row keeps the "Already in" its typed
 * total was entered against (`expectedReceived`), so a newer copy that moved it shows the
 * changed-since banner instead of silently moving the baseline (review F5).
 */
function reseedRows(previous: ReceiveRow[], items: PurchaseOrderItem[]): ReceiveRow[] {
  const typed = new Map(
    previous.filter((row) => row.touched).map((row) => [row.id, row])
  );
  return buildRows(items).map((row) => {
    const mine = typed.get(row.id);
    return mine
      ? {
          ...row,
          expectedReceived: mine.expectedReceived,
          receivedQtyText: mine.receivedQtyText,
          // A cost the operator typed stays, with the cost it was typed against; an untyped box
          // follows the newer copy (R2-3).
          ...(mine.costEdited
            ? {
                unitCostCents: mine.unitCostCents,
                costEdited: true,
                expectedUnitCost: mine.expectedUnitCost,
                ackUnitCase: mine.ackUnitCase,
              }
            : {}),
          expirationDate: mine.expirationDate,
          lotNumber: mine.lotNumber,
          touched: true,
        }
      : row;
  });
}

/** The banner's Reload: the operator has seen the new "Already in" and receives against it. */
function rebaselineRows(
  previous: ReceiveRow[],
  items: PurchaseOrderItem[]
): ReceiveRow[] {
  return reseedRows(previous, items).map((row) => ({
    ...row,
    expectedReceived: row.alreadyReceived,
    expectedUnitCost: row.currentUnitCost,
  }));
}

/** Touched rows whose "Already in" moved since the operator typed against it. */
function changedUnderOperator(rows: ReceiveRow[]): ReceiveRow[] {
  return rows.filter(
    (row) => row.touched && row.expectedReceived !== row.alreadyReceived
  );
}

/** Rows whose typed cost was entered against a cost an invoice has since changed (R2-3). */
function repricedUnderOperator(rows: ReceiveRow[]): ReceiveRow[] {
  return rows.filter(
    (row) => row.costEdited && row.expectedUnitCost !== row.currentUnitCost
  );
}

function ReceivePanel({ po }: { po: PurchaseOrder }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const items = useMemo(() => po.items ?? [], [po.items]);
  const today = localToday();

  const [rows, setRows] = useState<ReceiveRow[]>(() => buildRows(items));
  // TASK-0257 (DROP-2a): a read-only "with freight est." under each cost box when freight reaches this order.
  const landed = usePoLanded(po.id).data;
  const [invoiceNumber, setInvoiceNumber] = useState(po.invoiceNumber ?? '');
  const [notes, setNotes] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** TASK-0247: the "Add item to this delivery" dialog. */
  const [addOpen, setAddOpen] = useState(false);
  // TASK-0230 (owner rule C45): the day the goods arrived; a past day asks about counted items.
  // Today is offered only for an order sent in the last 2 days; an older order's delivery may be
  // days old, so the date is asked for (replan critique F4).
  const [arrivedOn, setArrivedOn] = useState(() => defaultArrivedOn(po, today));
  /**
   * Add-to-stock answers by `countAnswerKey` (PO item + the question shown); unticked (absent)
   * is the default for a line delivered before the count's day.
   */
  const [stockTicks, setStockTicks] = useState<Record<string, boolean>>({});
  /** The questions the server sent back with a 409, by PO item id, for this arrival day. */
  const [serverQuestions, setServerQuestions] = useState<{
    arrivedOn: string;
    byItem: Map<number, CountQuestion>;
  } | null>(null);
  /** The server refused with CHANGED_SINCE_OPENED; cleared only by the banner's Reload. */
  const [changedSinceOpened, setChangedSinceOpened] = useState(false);
  const [reloading, setReloading] = useState(false);
  /**
   * TASK-0236 (F2): receiving a draft asked "which PO?" (409 OPEN_ORDER_EXISTS): the vendor has an
   * order this delivery may belong to. Answered in a dialog; nothing was received.
   */
  const [openOrders, setOpenOrders] = useState<OpenOrderRefusal | null>(null);
  const [pickedOrder, setPickedOrder] = useState<number | null>(null);
  /** TASK-0236: the invoice number is already recorded on another PO (ALREADY_RECORDED_ON_ORDER). */
  const [recordedOn, setRecordedOn] = useState<{ poId: number; message: string } | null>(
    null
  );
  const liveInvoices = (po.invoices ?? []).filter((invoice) => invoice.voidedAt == null);
  const billedOnLabel =
    liveInvoices.length === 1
      ? `invoice ${liveInvoices[0]!.number ?? '(no number)'}`
      : liveInvoices.length > 1
        ? `${liveInvoices.length} invoices`
        : null;

  // TASK-0252 (INC-5): what each line's cost will do, from the same decision the API applies.
  const costFacts = useCostFacts(po.id);
  const factsByItem = new Map(
    (costFacts.data?.lines ?? []).map((line) => [line.itemId, line])
  );
  /** The preview for a row whose cost will be decided by this receipt; null when none is. */
  const costPreviewOf = (row: ReceiveRow, receivedAfter: number) => {
    const facts = factsByItem.get(row.id);
    if (!facts || receivedAfter <= 0 || row.unitCostCents == null) return null;
    const typedNew = row.costEdited && row.unitCostCents !== row.currentUnitCost;
    // A line billed by an invoice keeps its billed cost unless a different one is typed (INC-8).
    if (!typedNew && row.billedQty > 0) return null;
    const firstReceipt = row.alreadyReceived === 0 && receivedAfter > 0;
    if (!typedNew && !firstReceipt) return null;
    const raw = previewCost({
      line: facts,
      costCents: row.unitCostCents,
      thresholdPct: costFacts.data?.thresholdPct ?? 5,
      checkUnitCase: true,
      ack: false,
    });
    const shown = row.ackUnitCase
      ? previewCost({
          line: facts,
          costCents: row.unitCostCents,
          thresholdPct: costFacts.data?.thresholdPct ?? 5,
          checkUnitCase: true,
          ack: true,
        })
      : raw;
    return { shown, suspect: raw.needsTick };
  };

  // Seed the editable rows once per server copy of the order (its updatedAt), never on every
  // refetch: a background refetch must not wipe what the operator is typing.
  const seededFor = useRef(`${po.id}:${po.updatedAt ?? ''}`);
  // TASK-0232: warn when this vendor already has ANOTHER live order carrying the invoice number
  // being written here (this order is left out of the lookup).
  const invoiceNumberTooLong = invoiceNumber.trim().length > INVOICE_NUMBER_MAX_LENGTH;
  const dup = useDuplicateInvoiceCheck({
    vendorId: po.vendorId,
    invoiceNumber,
    excludePoId: po.id,
  });

  useEffect(() => {
    const stamp = `${po.id}:${po.updatedAt ?? ''}`;
    if (seededFor.current === stamp) return;
    seededFor.current = stamp;
    setRows((previous) => reseedRows(previous, items));
  }, [po.id, po.updatedAt, items]);

  const movedRows = changedUnderOperator(rows);
  const repricedRows = repricedUnderOperator(rows);
  const orderChanged =
    changedSinceOpened || movedRows.length > 0 || repricedRows.length > 0;

  /** Fetch the latest copy, then receive against its "Already in"; typed values stay. */
  const reload = async () => {
    setReloading(true);
    try {
      await queryClient.refetchQueries({ queryKey: poKeys.detail(po.id), exact: true });
    } finally {
      setReloading(false);
    }
    const latest = queryClient.getQueryData<PurchaseOrder>(poKeys.detail(po.id));
    setRows((previous) => rebaselineRows(previous, latest?.items ?? items));
    setChangedSinceOpened(false);
  };

  const patch = (id: number, next: Partial<ReceiveRow>) =>
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...next, touched: true } : row))
    );

  // Never below what is already in (an invoice or an earlier count may be above ordered).
  const receiveAll = () =>
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        receivedQtyText: quantityText(Math.max(row.orderedQty, row.alreadyReceived)),
        touched: true,
      }))
    );

  const checks = rows.map((row) => checkQuantity(row.receivedQtyText, row.unit));
  /**
   * What each row's box reads, for display. A box that does not read counts as
   * unchanged here; it is listed under problems and blocks the receive.
   */
  const receivedAt = (index: number): number =>
    checks[index]?.quantity ?? rows[index]?.alreadyReceived ?? 0;

  /** Only lines whose qty (or cost, on an already-moving line) changes. */
  const payloadRows = rows.flatMap((row, i) => {
    const received = checks[i]?.quantity ?? null;
    return received !== null && rowMoves(row, received) ? [{ ...row, received }] : [];
  });

  const rowProblems = rows.map((row, i) => {
    const out: string[] = [];
    const check = checks[i]!;
    if (check.problem !== null) {
      out.push(
        row.receivedQtyText.trim() === ''
          ? `${row.productName}: received quantity is blank. Enter the total received after this delivery; ${qty(row.alreadyReceived)} leaves the line as it is.`
          : `${row.productName}: received quantity ${check.problem}.`
      );
      return out;
    }
    const received = check.quantity;
    if (!rowMoves(row, received)) return out;
    if (received < row.alreadyReceived) {
      out.push(
        `${row.productName}: received qty cannot go down (already ${qty(row.alreadyReceived)}) — reverse the receipt instead.`
      );
    }
    // TASK-0247 (INC-3): more than ordered is accepted and flagged on the row, never refused.
    if (row.unit === 'ea' && !Number.isInteger(received)) {
      out.push(`${row.productName}: each-priced lines must receive a whole number.`);
    }
    return out;
  });
  const problems = rowProblems.flat();

  const missingExpiry = rows.filter(
    (row, i) =>
      row.needsExpiry && receivedAt(i) > row.alreadyReceived && !row.expirationDate
  );

  // Received total: what the order will hold once this goes in, and what this
  // delivery adds. Eaches and pounds are separate figures, never one sum.
  const inAfter = totalsByUnit(
    rows.map((row, i) => ({ row, i })),
    ({ row, i }) => ({
      unit: row.unit,
      quantity: receivedAt(i),
    })
  );
  const adding = totalsByUnit(
    rows.map((row, i) => ({ row, i })),
    ({ row, i }) => ({
      unit: row.unit,
      quantity: Math.max(0, receivedAt(i) - row.alreadyReceived),
    })
  );
  const orderedAll = totalsByUnit(rows, (row) => ({
    unit: row.unit,
    quantity: row.orderedQty,
  }));

  const receiveValue = rows.reduce(
    (sum, row, i) =>
      sum + Math.round((receivedAt(i) - row.alreadyReceived) * (row.unitCostCents ?? 0)),
    0
  );
  const costChanges = rows.filter(
    (row, i) =>
      receivedAt(i) > 0 &&
      row.costEdited &&
      row.unitCostCents != null &&
      row.unitCostCents !== row.currentUnitCost
  );
  // TASK-0252: rows whose 3x cost is not ticked: received anyway, the cost is not recorded.
  const costUnticked = rows.filter((row, i) => {
    const preview = costPreviewOf(row, receivedAt(i));
    return preview?.suspect === true && !row.ackUnitCase;
  });

  /* TASK-0230 (owner rule C45): lines adding stock to an item counted on or after the arrival
     day are asked about. The count check asks before submitting; the server asks again at
     submit, under its locks, and its 409 replaces the answer here. */
  const arrivedOnValid =
    /^\d{4}-\d{2}-\d{2}$/.test(arrivedOn) &&
    arrivedOn <= today &&
    arrivedOn >= addDays(today, -DELIVERY_DATE_WINDOW_DAYS);
  const addingRows = rows.filter(
    (row, i) => row.productId != null && receivedAt(i) > row.alreadyReceived
  );
  const countCheck = useCountCheck(
    arrivedOnValid ? arrivedOn : today,
    addingRows.map((row) => row.productId!),
    today
  );
  const questionByRow = new Map<number, CountQuestion>();
  if (arrivedOnValid && arrivedOn < today) {
    const byProduct = new Map(countCheck.questions.map((q) => [q.productId, q]));
    for (const row of addingRows) {
      const question = byProduct.get(row.productId!);
      if (question) questionByRow.set(row.id, { ...question, lineRef: `item:${row.id}` });
    }
    if (serverQuestions?.arrivedOn === arrivedOn) {
      for (const [itemId, question] of serverQuestions.byItem) {
        if (
          addingRows.some(
            (row) => row.id === itemId && row.productId === question.productId
          )
        )
          questionByRow.set(itemId, question);
      }
    }
  }
  const askedRows = addingRows.filter((row) => questionByRow.has(row.id));
  const askedLines = askedRows.map((row) => {
    const question = questionByRow.get(row.id)!;
    return {
      itemId: row.id,
      key: countAnswerKey(String(row.id), question),
      sameDayAsCount: question.sameDayAsCount,
    };
  });
  const unanswered = unansweredLines(askedLines, stockTicks);
  const leftOutIds = new Set(
    askedLines
      .filter((line) => stockAnswer(line, stockTicks) === false)
      .map((line) => line.itemId)
  );
  const leftOutRows = askedRows.filter((row) => leftOutIds.has(row.id));
  const checkingCounts = countCheck.pending;
  /** Why the confirm is held, when it is (the screen can change under an open dialog). */
  const confirmHeldBecause = checkingCounts
    ? 'Still checking which items were counted after this delivery.'
    : (unansweredMessage(unanswered.length) ??
      (orderChanged
        ? 'This order changed since you opened it. Close this and reload first.'
        : null));
  // What goes on hand: the adding lines, less the ones left out as counted (C45), per unit.
  const stocking = totalsByUnit(
    rows.map((row, i) => ({ row, i })),
    ({ row, i }) => ({
      unit: row.unit,
      quantity: leftOutRows.includes(row)
        ? 0
        : Math.max(0, receivedAt(i) - row.alreadyReceived),
    })
  );
  // What the person typed, then (when the invoice number is on another order) their answer.
  const receiveNotes = notesWithDuplicateAnswer(notes, dup.noteLine);

  const receive = useMutation({
    mutationFn: (answers?: {
      acknowledgeOpenOrderIds?: number[];
      keyOverrideDate?: string;
    }) => {
      // TASK-0230 review F2: refuses, nothing sent, while the count check for the date on screen
      // is still running or any asked line is unanswered.
      const decisions = answersToSend(
        askedLines,
        (line) => stockAnswer(line, stockTicks),
        checkingCounts
      );
      return api.post<PurchaseOrder>(`/vendors/purchase-orders/${po.id}/receive`, {
        // The one conversion from typed text; throws rather than send a
        // blank or unreadable box.
        items: rows
          .flatMap((row) => {
            const received = quantityForRequest(
              row.receivedQtyText,
              row.unit,
              row.productName
            );
            return rowMoves(row, received) ? [{ row, received }] : [];
          })
          .map(({ row, received }) => ({
            id: row.id,
            receivedQty: received,
            // The "Already in" the typed total was entered against: the API refuses the line
            // if someone else received it since (409 "changed since it was opened").
            expectedReceivedQty: row.expectedReceived,
            // Only a cost the operator typed, with the cost it was typed against (R2-3): a loaded
            // cost is the line's own and is never sent back as if typed.
            ...(row.costEdited && row.unitCostCents != null
              ? { unitCost: row.unitCostCents, expectedUnitCost: row.expectedUnitCost }
              : {}),
            // TASK-0252: the operator ticked "this is the unit cost" for a 3x cost.
            ...(row.ackUnitCase ? { ackUnitCase: true } : {}),
            // An empty string is a 400 — omit rather than send blank.
            ...(row.expirationDate ? { expirationDate: row.expirationDate } : {}),
            ...(row.lotNumber.trim() ? { lotNumber: row.lotNumber.trim() } : {}),
          })),
        ...(invoiceNumber.trim() ? { invoiceNumber: invoiceNumber.trim() } : {}),
        ...(receiveNotes ? { notes: receiveNotes } : {}),
        arrivedOn,
        ...(decisions.length > 0
          ? {
              stockDecisions: decisions.map(({ line, addStock }) => ({
                itemId: line.itemId,
                addStock,
              })),
            }
          : {}),
        // TASK-0236: "a different delivery" for every order the which-PO step listed; the
        // key-by-date answer for a vendor that reuses invoice numbers.
        ...(answers?.acknowledgeOpenOrderIds?.length
          ? { acknowledgeOpenOrderIds: answers.acknowledgeOpenOrderIds }
          : {}),
        ...(answers?.keyOverrideDate ? { keyOverrideDate: answers.keyOverrideDate } : {}),
      });
    },
    onSuccess: (updated) => {
      // Received: start over from the server's copy (nothing typed is pending any more).
      seededFor.current = `${po.id}:${updated.updatedAt ?? ''}`;
      setRows(buildRows(updated.items ?? []));
      queryClient.setQueryData(poKeys.detail(po.id), updated);
      invalidateAfterReceive(queryClient);
      setConfirmOpen(false);
      setRefusal(null);
      setOpenOrders(null);
      setRecordedOn(null);
      // A cost that waits for its check is said once, below (not again as a follow-up).
      const costChecked = new Set(
        (updated.warnings ?? [])
          .filter((w) => w.kind === 'cost_needs_check')
          .map((w) => w.itemId)
      );
      const flagged = new Set(
        (updated.warnings ?? [])
          .filter(
            (w) =>
              w.kind !== 'cost_needs_check' &&
              !(w.kind === 'cost_pending' && costChecked.has(w.itemId))
          )
          .map((w) => w.itemId)
      ).size;
      const flaggedNote =
        flagged > 0
          ? ` ${flagged === 1 ? '1 line' : `${flagged} lines`} flagged for a second look in Follow-ups.`
          : '';
      // TASK-0252: a 3x cost was not recorded; the stock is in and the cost waits for the office. On a
      // line that already had a cost (review W7), the line kept it: nothing waits, the typed one was not used.
      const costChecks = (updated.warnings ?? []).filter(
        (w) => w.kind === 'cost_needs_check'
      );
      const costKept = costChecks.filter((w) => w.keptUnitCostCents != null);
      const costWaiting = costChecks.filter((w) => w.keptUnitCostCents == null);
      if (costKept.length > 0) {
        toast.warning(
          costKept.length === 1
            ? `${money(costKept[0]!.typedUnitCostCents ?? null)} was not used: check case vs unit. The line kept its cost of ${money(costKept[0]!.keptUnitCostCents!)}.`
            : `${costKept.length} typed costs were not used: check case vs unit. Those lines kept their costs.`
        );
      }
      if (costWaiting.length > 0) {
        toast.warning(
          `${countLabel(costWaiting.length, 'cost')} waiting: check case vs unit. The stock is in; enter the ${costWaiting.length === 1 ? 'cost' : 'costs'} under Invoice costs.`,
          {
            action: {
              label: 'Enter costs',
              onClick: () => void navigate({ search: { mode: 'costs' } }),
            },
          }
        );
      }
      if (updated.status === 'received') {
        toast.success(
          po.closedShortAt
            ? `Backorder received on PO #${po.id}.${flaggedNote}`
            : `PO #${po.id} fully received — every ordered unit is in.${flaggedNote}`
        );
      } else if (flaggedNote) {
        toast.success(`Receipt recorded on PO #${po.id}.${flaggedNote}`);
      } else {
        const outstanding = totalsByUnit(updated.items ?? [], (item) => ({
          unit: item.unit,
          quantity: Math.max(0, n(item.orderedQty) - n(item.receivedQty)),
        }));
        toast.success(
          `Partial receipt recorded on PO #${po.id} — ${unitTotalsText(outstanding)} still outstanding.`
        );
      }
      void navigate({ search: { mode: 'lines' } });
    },
    onError: (error) => {
      setConfirmOpen(false);
      // TASK-0230: the server asked about counted items (a count landed since the check, or
      // the check had not answered yet). Show its questions; nothing was received.
      const asked = countQuestionsFromError(error);
      if (asked) {
        const byItem = new Map<number, CountQuestion>();
        for (const question of asked) {
          const itemId = Number(question.lineRef.replace(/^item:/, ''));
          if (Number.isInteger(itemId)) byItem.set(itemId, question);
        }
        setServerQuestions({ arrivedOn, byItem });
        setRefusal(null);
        toast.message(
          'Some items were counted after this delivery. Choose for each, then receive again.'
        );
        return;
      }
      // TASK-0230: someone received this order since the screen was opened. Keep what was
      // typed and offer a reload so the operator can reconcile against the new "Already in".
      if (refusalCode(error) === CHANGED_SINCE_OPENED) {
        setChangedSinceOpened(true);
        setRefusal(null);
        return;
      }
      // TASK-0236 (F2): a draft's delivery may be one of the vendor's open orders. Ask which.
      const asked409 = openOrdersFromError(error);
      if (asked409) {
        setOpenOrders(asked409);
        setPickedOrder(asked409.preselectPoId);
        setRefusal(null);
        return;
      }
      const recorded = recordedOnOrderFromError(error);
      if (recorded) {
        setRecordedOn({ poId: recorded.poId, message: recorded.message });
        setRefusal(null);
        return;
      }
      // A 409 is a refusal, not a fault. Show the server's sentence in place, not
      // as a disappearing toast. Receiving into a negative on-hand is allowed —
      // a receipt is a fact (docs/DECISIONS.md) — so it is never the reason.
      if (error instanceof ApiError && error.isConflict) setRefusal(error.message);
      else toast.error(errorMessage(error));
    },
  });

  return (
    <Panel
      title={po.closedShortAt ? 'Backorder arrived' : 'Receive against this order'}
      description={
        po.closedShortAt
          ? 'This order was closed short. Enter what came of the backorder: it is received on this order, which stays received.'
          : 'Enter the cumulative received quantity per line — this is a money and inventory path, so nothing is applied until you confirm.'
      }
      actions={
        <>
          {po.status !== 'draft' ? (
            <Button
              size="xs"
              icon={<Plus className="size-3" />}
              onClick={() => setAddOpen(true)}
            >
              Add item to this delivery
            </Button>
          ) : null}
          <Button
            size="xs"
            icon={<ClipboardCheck className="size-3" />}
            onClick={receiveAll}
          >
            Receive all
          </Button>
        </>
      }
      bleed
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="font-mono tabular-nums">
            <div>
              Received total after this delivery:{' '}
              <span className="text-ink" data-testid="received-total">
                {unitTotalsText(inAfter, '0')}
              </span>{' '}
              <span className="text-ink-faint">
                of {unitTotalsText(orderedAll, '0')} ordered
              </span>
            </div>
            <div className="text-ink-faint">
              This delivery adds {unitTotalsText(adding, 'nothing')} ·{' '}
              {countLabel(payloadRows.length, 'line')} · {money(receiveValue)} of goods
            </div>
          </div>
          <Button
            size="sm"
            variant="primary"
            icon={<PackageCheck className="size-3.5" />}
            disabled={
              payloadRows.length === 0 ||
              problems.length > 0 ||
              !arrivedOnValid ||
              checkingCounts ||
              unanswered.length > 0 ||
              orderChanged ||
              invoiceNumberTooLong
            }
            loading={checkingCounts}
            onClick={() => setConfirmOpen(true)}
          >
            Review &amp; receive
          </Button>
        </div>
      }
    >
      {orderChanged ? (
        <Callout
          tone="negative"
          className="m-3"
          title="This order changed since you opened it"
          actions={
            <Button
              size="xs"
              variant="primary"
              loading={reloading}
              onClick={() => void reload()}
            >
              Reload
            </Button>
          }
        >
          {changedSinceOpened
            ? 'Some of these lines changed after you opened this screen (received, or re-priced by an invoice), so nothing was received. Reload to see the new “Already in” amounts and costs.'
            : movedRows.length > 0
              ? `Someone received ${movedRows.map((row) => row.productName).join(', ')} after you started entering this delivery, so “Already in” moved. Reload to receive against the new amounts.`
              : `An invoice changed the cost of ${repricedRows.map((row) => row.productName).join(', ')} after you typed it. Reload to see the new cost.`}{' '}
          What you typed stays, so check each line against the delivery before receiving
          again.
        </Callout>
      ) : null}

      {recordedOn ? (
        <Callout
          tone="negative"
          className="m-3"
          title={recordedOn.message}
          actions={
            <>
              <Button size="xs" asChild>
                <Link
                  to="/purchasing/$id"
                  params={{ id: String(recordedOn.poId) }}
                  search={{ mode: 'invoices' as const }}
                >
                  Open PO #{recordedOn.poId}
                </Link>
              </Button>
              <Button
                size="xs"
                variant="ghost"
                loading={receive.isPending}
                onClick={() => receive.mutate({ keyOverrideDate: arrivedOn || today })}
              >
                This vendor reuses invoice numbers: key it by date
              </Button>
            </>
          }
        >
          Nothing was received.
        </Callout>
      ) : null}

      {refusal ? (
        <div className="border-negative/40 bg-negative-muted m-3 rounded border p-3">
          <div className="text-ink text-xs font-medium">The API refused this receipt</div>
          <p className="text-2xs text-ink-muted mt-1 font-mono leading-relaxed whitespace-pre-wrap">
            {refusal}
          </p>
          <p className="text-2xs text-ink-faint mt-2">
            Nothing was received. Reload the order to see its current lines, then receive
            again.
          </p>
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[52rem] text-xs">
          <thead>
            <tr className="border-line bg-surface-sunken text-2xs text-ink-faint border-b tracking-wider uppercase">
              <th className="px-3 py-1.5 text-left font-medium">Product</th>
              <th className="w-20 px-2 py-1.5 text-right font-medium">Ordered</th>
              <th className="w-20 px-2 py-1.5 text-right font-medium">Already in</th>
              <th className="w-32 px-2 py-1.5 text-right font-medium">
                Total received after this delivery
              </th>
              <th className="w-28 px-2 py-1.5 text-right font-medium">Unit cost</th>
              <th className="w-32 px-2 py-1.5 text-left font-medium">vs ordered</th>
              <th className="w-56 px-3 py-1.5 text-left font-medium">Expiry / lot</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const delta = receivedAt(index) - row.alreadyReceived;
              const complete = row.orderedQty > 0 && receivedAt(index) >= row.orderedQty;
              const flags = receiveLineFlags({
                unit: row.unit,
                orderedQty: row.orderedQty,
                receivedAfter: receivedAt(index),
              });
              const expiryMissing = row.needsExpiry && delta > 0 && !row.expirationDate;
              return (
                <tr
                  key={row.id}
                  className={
                    delta > 0
                      ? 'border-line-soft bg-signal-muted/40 border-b last:border-0'
                      : 'border-line-soft border-b last:border-0'
                  }
                >
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-ink truncate">{row.productName}</span>
                      {row.unit === 'lb' ? <Badge tone="info">lb</Badge> : null}
                      {complete ? <Badge tone="positive">complete</Badge> : null}
                      {flags.map((flag) => (
                        <Badge key={flag.badge} tone={flag.tone}>
                          {flag.badge}
                        </Badge>
                      ))}
                      {questionByRow.has(row.id) ? (
                        <Badge tone="warn">
                          Last counted{' '}
                          {storeDayLabel(questionByRow.get(row.id)!.lastCountedOn)}
                        </Badge>
                      ) : null}
                    </div>
                    {row.billedQty > 0 ? (
                      <div className="text-2xs text-ink-muted">
                        Billed: {qty(row.billedQty)}
                        {billedOnLabel ? ` on ${billedOnLabel}` : ''}
                      </div>
                    ) : null}
                    {row.fromInvoices.length > 0 ? (
                      <div className="text-2xs text-ink-muted">
                        Checked in from{' '}
                        {row.fromInvoices
                          .map(
                            (r) => `invoice ${r.number ?? '(no number)'} (${qty(r.qty)})`
                          )
                          .join(', ')}
                      </div>
                    ) : null}
                    {flags
                      .filter((flag) => flag.note)
                      .map((flag) => (
                        <div key={flag.badge} className="text-2xs text-warn">
                          {flag.note}
                        </div>
                      ))}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                    {qty(row.orderedQty)}
                  </td>
                  <td className="text-ink-faint px-2 py-1.5 text-right font-mono tabular-nums">
                    {qty(row.alreadyReceived)}
                  </td>
                  <td className="px-2 py-1.5">
                    <QuantityInput
                      size="sm"
                      className="w-full"
                      unit={row.unit}
                      aria-label={`Received quantity for ${row.productName}`}
                      value={row.receivedQtyText}
                      invalid={(rowProblems[index]?.length ?? 0) > 0}
                      onValueChange={(text) => patch(row.id, { receivedQtyText: text })}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <MoneyInput
                      size="sm"
                      value={row.unitCostCents}
                      aria-label={`Unit cost for ${row.productName}`}
                      onValueChange={(cents) => {
                        // The box reports on every blur: only a changed value is an edit.
                        if (cents !== row.unitCostCents)
                          patch(row.id, {
                            unitCostCents: cents,
                            costEdited: true,
                            ackUnitCase: false,
                          });
                      }}
                    />
                    {row.currentUnitCost == null && row.orderedUnitCost != null ? (
                      <div
                        className="text-2xs text-ink-faint mt-0.5 text-right whitespace-nowrap"
                        title="An estimate never becomes the cost: leave it blank to keep the item's cost, or type the invoice cost."
                      >
                        ordered at {money(row.orderedUnitCost)} est.
                      </div>
                    ) : null}
                    {(() => {
                      const line = landed?.applies
                        ? landed.lines.find((l) => l.poItemId === row.id)
                        : undefined;
                      const cost =
                        row.unitCostCents ?? row.currentUnitCost ?? row.orderedUnitCost;
                      if (!line?.perUnitExact || cost == null) return null;
                      return (
                        <div
                          className="text-2xs text-ink-faint mt-0.5 text-right whitespace-nowrap"
                          title="Cost with freight (landed cost: what it cost to get it onto our shelf). Shown only; nothing here is posted."
                          data-testid={`receive-freight-${row.id}`}
                        >
                          with freight est.{' '}
                          {money(Math.round(cost + Number(line.perUnitExact)))}
                        </div>
                      );
                    })()}
                    {(() => {
                      const preview = costPreviewOf(row, receivedAt(index));
                      if (!preview) return null;
                      return (
                        <div
                          className="mt-0.5 text-right"
                          data-testid={`cost-preview-${row.id}`}
                        >
                          <div
                            className={cn(
                              'text-2xs font-medium whitespace-nowrap',
                              preview.suspect && !row.ackUnitCase
                                ? 'text-negative'
                                : preview.shown.decision.decision === 'pending'
                                  ? 'text-warn'
                                  : preview.shown.decision.decision === 'applied'
                                    ? 'text-positive'
                                    : 'text-ink-muted'
                            )}
                            title={preview.shown.detail}
                          >
                            {preview.suspect && !row.ackUnitCase
                              ? 'Check case vs unit'
                              : preview.shown.label}
                          </div>
                          {preview.suspect ? (
                            <Checkbox
                              className="mt-0.5 justify-end"
                              checked={row.ackUnitCase}
                              onCheckedChange={(checked) =>
                                patch(row.id, { ackUnitCase: checked })
                              }
                              label={<span className="text-2xs">Unit cost</span>}
                            />
                          ) : null}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-1.5">
                    <CostVariance from={row.orderedUnitCost} to={row.unitCostCents} />
                  </td>
                  <td className="px-3 py-1.5">
                    {row.needsExpiry ? (
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="date"
                          size="sm"
                          mono
                          className="w-32"
                          aria-label={`Expiration date for ${row.productName}`}
                          value={row.expirationDate}
                          invalid={expiryMissing}
                          onChange={(e) =>
                            patch(row.id, { expirationDate: e.currentTarget.value })
                          }
                        />
                        <Input
                          size="sm"
                          className="w-20"
                          placeholder="Lot"
                          aria-label={`Lot number for ${row.productName}`}
                          value={row.lotNumber}
                          maxLength={128}
                          onChange={(e) =>
                            patch(row.id, { lotNumber: e.currentTarget.value })
                          }
                        />
                      </div>
                    ) : (
                      <span className="text-2xs text-ink-faint">not tracked</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="border-line grid gap-3 border-t p-3 sm:grid-cols-3">
        <Field
          label="Arrived on"
          required
          hint={
            arrivedOn === ''
              ? 'When did this delivery arrive? Use the date on the delivery slip.'
              : 'The day the goods came in. A past day asks about items counted since.'
          }
          error={
            arrivedOn === '' || arrivedOnValid
              ? null
              : 'Arrived on must be a date in the last 60 days.'
          }
        >
          <Input
            type="date"
            size="sm"
            mono
            aria-label="Arrived on"
            value={arrivedOn}
            min={addDays(today, -DELIVERY_DATE_WINDOW_DAYS)}
            max={today}
            invalid={arrivedOn !== '' && !arrivedOnValid}
            onChange={(e) => {
              setArrivedOn(e.currentTarget.value);
              setServerQuestions(null);
            }}
          />
        </Field>
        <Field
          label="Invoice number"
          hint="Written onto the PO header."
          error={invoiceNumberTooLong ? INVOICE_NUMBER_TOO_LONG_MESSAGE : undefined}
        >
          <Input
            size="sm"
            mono
            value={invoiceNumber}
            invalid={invoiceNumberTooLong}
            onChange={(e) => setInvoiceNumber(e.currentTarget.value)}
          />
        </Field>
        <Field label="Receiving note" hint="Appended to the PO's existing notes.">
          <Input
            size="sm"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
          />
        </Field>
      </div>

      {askedRows.length > 0 ? (
        <CountConfirmation
          className="mx-3 mb-3"
          lines={askedRows.map((row) => {
            const question = questionByRow.get(row.id)!;
            return {
              ...question,
              key: countAnswerKey(String(row.id), question),
              name: row.productName,
            };
          })}
          decisions={stockTicks}
          onChange={(key, addStock) =>
            setStockTicks((prev) => ({ ...prev, [key]: addStock }))
          }
        />
      ) : null}

      {unansweredMessage(unanswered.length) ? (
        <p className="text-2xs text-warn mx-3 mb-3">
          {unansweredMessage(unanswered.length)}
        </p>
      ) : null}

      <DuplicateInvoiceCallout
        check={dup}
        vendorName={po.vendorName ?? null}
        invoiceNumber={invoiceNumber}
        onStop={() => void navigate({ search: { mode: 'lines' } })}
        className="mx-3 mb-3"
      />

      {problems.length > 0 ? (
        <ul className="border-negative/30 bg-negative-muted text-2xs text-negative mx-3 mb-3 space-y-1 rounded border p-2">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}

      {missingExpiry.length > 0 ? (
        <p className="border-warn/30 bg-warn-muted text-2xs text-ink mx-3 mb-3 rounded border p-2">
          {countLabel(missingExpiry.length, 'perishable line')} have no expiry date. The
          receipt is still accepted — the delivery is a fact — but those lots land undated
          and show up on Inventory → Expiring as a data-quality nudge.
        </p>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Receive ${countLabel(payloadRows.length, 'line')} on PO #${po.id}?`}
        description="Check the lines below. Receiving adds the stock and updates cost."
        confirmLabel="Receive stock"
        variant="primary"
        loading={receive.isPending}
        confirmDisabled={confirmHeldBecause !== null}
        consequences={[
          `Adds ${unitTotalsText(stocking, 'nothing')} to on-hand inventory.`,
          ...(countConfirmationConsequence(leftOutRows.length)
            ? [countConfirmationConsequence(leftOutRows.length)!]
            : []),
          `Books ${money(receiveValue)} of goods against this order.`,
          ...overReceiptConsequences(
            rows.map((row, i) => ({
              unit: row.unit,
              orderedQty: row.orderedQty,
              receivedAfter: receivedAt(i),
              moves: receivedAt(i) > row.alreadyReceived,
            }))
          ),
          costChanges.length > 0
            ? `${countLabel(costChanges.length, 'line')} carry a new unit cost — above the approval threshold they park in Cost approvals instead of applying.`
            : 'No unit costs change.',
          ...(costUnticked.length > 0
            ? [
                `${countLabel(costUnticked.length, 'cost')} 3 times or more from what the item usually costs ${costUnticked.length === 1 ? 'is' : 'are'} not recorded unless ticked: the stock comes in and the cost waits under Invoice costs.`,
              ]
            : []),
          ...(dup.noteLine ? [`The order's notes will say “${dup.noteLine}”.`] : []),
        ]}
        onConfirm={() => receive.mutate(undefined)}
      >
        {confirmHeldBecause ? (
          <p className="text-2xs text-warn mb-2">{confirmHeldBecause}</p>
        ) : null}
        <div className="border-line bg-surface-sunken text-2xs max-h-56 space-y-1 overflow-y-auto rounded border p-2">
          {payloadRows.map((row) => (
            <div key={row.id} className="flex items-baseline justify-between gap-2">
              <span className="text-ink min-w-0 truncate">{row.productName}</span>
              <span className="text-ink-muted shrink-0 font-mono tabular-nums">
                +{qty(row.received - row.alreadyReceived)} @ {money(row.unitCostCents)}
              </span>
            </div>
          ))}
        </div>
      </ConfirmDialog>

      {addOpen ? (
        <AddItemDialog
          po={po}
          onClose={() => setAddOpen(false)}
          onAdded={(updated) => {
            queryClient.setQueryData(poKeys.detail(po.id), updated);
            setAddOpen(false);
          }}
        />
      ) : null}

      {openOrders ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setOpenOrders(null);
          }}
          title="Which PO is this delivery for?"
          description={`Nothing was received. ${po.vendorName ?? 'This vendor'} has an order this delivery may belong to.`}
          width="lg"
        >
          <WhichOrderStep
            vendorName={po.vendorName ?? null}
            candidates={openOrders.orders}
            selectedPoId={pickedOrder}
            onSelect={setPickedOrder}
            orderActionLabel={(id) => `Enter on PO #${id}`}
            quantities={false}
            onUseOrder={(id) => {
              setOpenOrders(null);
              void navigate({
                to: '/purchasing/$id/invoice',
                params: { id: String(id) },
                search: { mode: 'manual' },
              });
            }}
            onDifferent={() => {
              const ids = openOrders.orders.map((order) => order.poId);
              setOpenOrders(null);
              receive.mutate({ acknowledgeOpenOrderIds: ids });
            }}
            busy={receive.isPending}
          />
          <p className="text-2xs text-ink-faint mt-2">
            Enter on that PO leaves this draft as it is; cancel it from here if it is not
            needed.
          </p>
        </Dialog>
      ) : null}
    </Panel>
  );
}

/**
 * TASK-0247 (INC-3): add an item that came but was not ordered to this order, so it can be received on
 * it. The line joins the table with ordered 0 and a "not ordered" badge; nothing is received until the
 * Receive tab's confirm.
 */
function AddItemDialog({
  po,
  onClose,
  onAdded,
}: {
  po: PurchaseOrder;
  onClose: () => void;
  onAdded: (updated: PurchaseOrder) => void;
}) {
  const [picked, setPicked] = useState<PickedProduct | null>(null);
  const [error, setError] = useState<string | null>(null);
  const onOrder = useMemo(
    () =>
      new Set(
        (po.items ?? []).flatMap((item) =>
          item.productId != null ? [Number(item.productId)] : []
        )
      ),
    [po.items]
  );
  const add = useMutation({
    mutationFn: (product: PickedProduct) =>
      api.post<PurchaseOrder>(`/vendors/purchase-orders/${po.id}/lines`, {
        productId: product.id,
        unit: product.unit,
        orderedQty: 0,
        expectedUpdatedAt: po.updatedAt,
      }),
    onSuccess: (updated) => {
      toast.success(
        `${picked?.name ?? 'The item'} added to PO #${po.id}: enter what came, then receive.`
      );
      onAdded(updated);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Add item to this delivery"
      description="An item that came but was not on the order. It joins the order as not ordered; you then enter what came and receive it with the rest."
      width="lg"
    >
      <div className="space-y-3">
        <ProductPicker
          vendorId={po.vendorId}
          autoFocus
          disabledIds={onOrder}
          onPick={(product) => {
            setPicked(product);
            setError(null);
          }}
        />
        {picked ? (
          <p className="text-xs text-ink" data-testid="add-item-picked">
            {picked.name}
            {picked.unit === 'lb' ? ' (by the pound)' : ''}
          </p>
        ) : (
          <p className="text-2xs text-ink-faint">
            Search by name, item number or barcode. Items already on this order are shown
            but cannot be added again: use their line.
          </p>
        )}
        {error ? (
          <Callout tone="negative" title="Not added">
            {error}
          </Callout>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            icon={<Plus className="size-3.5" />}
            disabled={picked === null}
            loading={add.isPending}
            onClick={() => picked && add.mutate(picked)}
          >
            Add to this order
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
